import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './index.module.less';
import SearchInput, { FowardProps } from '@/components/SearchInput';
import Panel from '@/components/Panel';
import QuestionList, { ItemProps } from '@/components/QuestionList';
import cloneDeep from 'lodash/cloneDeep';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import Icon from '@/components/Icon';
import { getHotList, getAllList } from '@/utils/getData';
import { searchLogic } from './searchLogic';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StoreType } from '@/store';
import { setSearchInfo } from '@/store/search/action';
import { helpCenterTracking } from '@/utils/track';

const ALL_LIST = getAllList();
const HOT_LIST = getHotList();

const Search: React.FC = () => {
  const [value, setValue] = useState('');
  const [header, setHeader] = useState('');
  const [list, setList] = useState<ItemProps[]>([]);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  // 输入框是否聚焦
  const [isFocus, setIsFocus] = useState(false);

  const inputRef = useRef<FowardProps>(null);

  const { t } = useTranslation();

  const searchInfo = useSelector((state: StoreType) => state.search);
  const dispatch = useDispatch();

  // 关键字高亮：title 中与关键词匹配的单词（不论大小写）都需高亮
  const matchHighlightKeywords = (list: ItemProps[], keywordList: string[]) => {
    let results = [];
    // 对每个 keyword 生成一个正则，不区分大小写
    const regList = [];
    if (keywordList.length > 0) {
      keywordList.forEach((item: string) => {
        regList.push(new RegExp(item, 'gi'));
      });
    }
    // 更改匹配词样式
    results = list.map((item: ItemProps) => {
      if (regList.length > 0) {
        let matchWordsRegList = [];
        regList.forEach((i) => {
          // 获取 title 中 匹配到 keyword 的词，同时对这些词逐一生成正则，区分大小写
          const matchWords = item.title.match(i);
          matchWords &&
            matchWords.forEach((word) => {
              matchWordsRegList.push({
                word: word,
                reg: new RegExp(word, 'g'),
              });
            });
        });
        // 去重
        matchWordsRegList = uniqBy(matchWordsRegList, 'word');
        // 替换 title 中 匹配词 样式
        matchWordsRegList.forEach((reg) => {
          item.title = item.title.replace(
            reg.reg,
            `<span class="${styles.searchText}">${reg.word}</span>`,
          );
        });
      }
      return item;
    });
    return results;
  };

  // 模糊搜索
  const search = useCallback(() => {
    let resultList: ItemProps[] = [];
    const keywordObj = cloneDeep(ALL_LIST);
    if (Object.keys(keywordObj).length > 0) {
      // 如果不为空对象，遍历 ALL_LIST，生成渲染用的数组结构
      const res = searchLogic(value, keywordObj);
      // 匹配结果
      resultList = res.result;
      // 去重，存在一个 fileName 同时存在于多个 keyword列表中的场景
      resultList = uniqBy(resultList, 'fileName');

      // 匹配到的关键字集合，关键字高亮
      const highlightKeywords = uniq(res.highlightKeywords);
      resultList = matchHighlightKeywords(resultList, highlightKeywords);
    }
    setList(resultList);
    if (!resultList.length) {
      setIsEmpty(true);
    } else {
      setIsEmpty(false);
      helpCenterTracking({
        operation: 'view',
        page: 'help_centre_search_result',
      });
    }
    dispatch(
      setSearchInfo({
        value: value,
      }),
    );
  }, [value, dispatch]);

  useEffect(() => {
    if (!value) {
      setHeader('faq_full');
      setList(HOT_LIST);
    } else {
      setHeader('search_results');
      search();
    }
  }, [value, search]);

  useEffect(() => {
    // 如果有store，需要使用store值
    if (searchInfo.value) {
      setValue(searchInfo.value);
    }
  }, [searchInfo]);

  const onScroll = useCallback(() => {
    // 开始滚动时，需要隐藏键盘
    if (isFocus) {
      setIsFocus(false);
      inputRef.current?.blur();
    }
  }, [isFocus]);

  const onPressReturn = useCallback((code: number) => {
    if (code === 13) {
      inputRef.current.blur();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', (event) => {
      onPressReturn(event.keyCode);
    });
    return () => {
      window.removeEventListener('keydown', (event) => {
        onPressReturn(event.keyCode);
      });
    };
  }, [onPressReturn]);

  const handleonClear = useCallback(() => {
    setValue('');
    dispatch(
      setSearchInfo({
        value: '',
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    helpCenterTracking({
      operation: 'view',
      page: 'help_centre_search_lp',
    });
  }, []);

  useEffect(() => {
    if (isEmpty) {
      helpCenterTracking({
        operation: 'view',
        page: 'help_centre_search_result_empty',
      });
    }
  }, [isEmpty, list]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <SearchInput
          value={value}
          onClear={() => handleonClear()}
          onChange={(e: string) => setValue(e)}
          autoFocus={true}
          onFocus={() => setIsFocus(true)}
          ref={inputRef}
          placeholder={'enter_question'}
        />
      </div>
      <div className={styles.scrollBox} onScroll={onScroll}>
        {list && list.length > 0 ? (
          <Panel header={t(header)}>
            <QuestionList
              list={list}
              onTrack={() =>
                helpCenterTracking({
                  operation: 'view',
                  page: 'help_centre_search_result',
                })
              }
            />
          </Panel>
        ) : value ? (
          <div className={styles.emptyBox}>
            <Icon iconName={'empty_search'} imgClass={styles.iconClass} />
            <p>{t('no_search_results', { search: value })}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Search;
